/**
 * DEVELOPMENT ONLY — the demo's tables shared through Supabase.
 *
 * The mock repositories keep all their logic; MockDb just saves its rows here
 * as well as in IndexedDB, and loads them from here when the app opens, so
 * everybody who opens the site sees one family. Pictures and recordings go to
 * the `dev-blobs` Storage bucket. Other devices hear about a change over a
 * Realtime broadcast carrying the table names, and fetch those tables again.
 *
 * Rows are stored one per record (supabase/migrations/…08_dev_shared_store.sql):
 * two people changing different relatives at the same moment do not overwrite
 * each other; the same field on the same record is last-write-wins.
 *
 * On when VITE_SHARED_DEV_STORE=on and the Supabase address and key are set.
 * Replaced by the real repositories later — see docs/BACKEND_INTEGRATION.md.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabase } from '../supabase/client';

type Rows = unknown[];

/** Tables that describe this device, not the family: never shared. */
const LOCAL_ONLY = new Set(['recording_drafts', 'pending_operations', 'idempotency']);
/** Recording chunks are this device's work in progress; the finished recording is shared. */
const LOCAL_BLOB = /^chunk\//;
const BUCKET = 'dev-blobs';
const PAGE = 1000;
const WRITE_BATCH = 500;

function env(name: string): string | undefined {
  // import.meta.env only exists under Vite; the unit tests run in plain Node.
  return (import.meta as { env?: Record<string, string | undefined> }).env?.[name];
}

export function sharedStoreEnabled(): boolean {
  return env('VITE_SHARED_DEV_STORE') === 'on' && !!env('VITE_SUPABASE_URL') && !!env('VITE_SUPABASE_PUBLISHABLE_KEY');
}

export function isShared(table: string): boolean {
  return !LOCAL_ONLY.has(table);
}

/** A row's identity within its table. Almost every row has an id; tree_settings is one per family. */
function rowKey(row: Record<string, unknown>): string | undefined {
  const k = row.id ?? row.familyId ?? row.key;
  return typeof k === 'string' ? k : undefined;
}

export class SharedStore {
  /** What the server holds, as last seen by this device: table → id → JSON. */
  private seen = new Map<string, Map<string, string>>();
  private channel: RealtimeChannel | null = null;

  /** Every shared table, or null when the shared store is still empty (the first visitor seeds it). */
  async loadAll(): Promise<Record<string, Rows> | null> {
    const out: Record<string, Rows> = {};
    let from = 0;
    for (;;) {
      const { data, error } = await getSupabase()
        .from('dev_rows').select('tbl,id,data').order('seq').range(from, from + PAGE - 1);
      if (error) throw error;
      for (const r of data) (out[r.tbl] ??= []).push(r.data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    if (!Object.keys(out).length) return null;
    for (const [tbl, rows] of Object.entries(out)) this.remember(tbl, rows);
    return out;
  }

  /** Fetch these tables again after another device changed them. */
  async fetchTables(tables: string[]): Promise<Record<string, Rows>> {
    const wanted = tables.filter(isShared);
    const out: Record<string, Rows> = Object.fromEntries(wanted.map((t) => [t, [] as Rows]));
    if (!wanted.length) return out;
    let from = 0;
    for (;;) {
      const { data, error } = await getSupabase()
        .from('dev_rows').select('tbl,id,data').in('tbl', wanted).order('seq').range(from, from + PAGE - 1);
      if (error) throw error;
      for (const r of data) out[r.tbl].push(r.data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    for (const [tbl, rows] of Object.entries(out)) this.remember(tbl, rows);
    return out;
  }

  /**
   * Send what changed in these tables since this device last saw them: new and
   * edited rows are upserted, rows that are gone are deleted. Then tell the
   * other devices which tables to fetch.
   */
  async push(tables: Record<string, Rows>): Promise<void> {
    const sb = getSupabase();
    const changed: string[] = [];
    for (const [tbl, rows] of Object.entries(tables)) {
      if (!isShared(tbl)) continue;
      const before = this.seen.get(tbl) ?? new Map<string, string>();
      const now = new Map<string, string>();
      const upserts: Array<{ tbl: string; id: string; data: unknown; updated_at: string }> = [];
      const stamp = new Date().toISOString();
      for (const row of rows) {
        const id = rowKey(row as Record<string, unknown>);
        if (!id) continue;
        const json = JSON.stringify(row);
        now.set(id, json);
        if (before.get(id) !== json) upserts.push({ tbl, id, data: row, updated_at: stamp });
      }
      const gone = [...before.keys()].filter((id) => !now.has(id));
      for (let i = 0; i < upserts.length; i += WRITE_BATCH) {
        const { error } = await sb.from('dev_rows').upsert(upserts.slice(i, i + WRITE_BATCH), { onConflict: 'tbl,id' });
        if (error) throw error;
      }
      for (let i = 0; i < gone.length; i += WRITE_BATCH) {
        const { error } = await sb.from('dev_rows').delete().eq('tbl', tbl).in('id', gone.slice(i, i + WRITE_BATCH));
        if (error) throw error;
      }
      this.seen.set(tbl, now);
      if (upserts.length || gone.length) changed.push(tbl);
    }
    if (changed.length) await this.channel?.send({ type: 'broadcast', event: 'changed', payload: { tables: changed } });
  }

  /** Empty the shared store (Prototype → Reset demo data). */
  async clear(): Promise<void> {
    const { error } = await getSupabase().from('dev_rows').delete().neq('tbl', '');
    if (error) throw error;
    this.seen.clear();
    await this.channel?.send({ type: 'broadcast', event: 'changed', payload: { tables: ['*'] } });
  }

  /** Hear about other devices' changes. The sender does not hear its own. */
  listen(onChange: (tables: string[]) => void): void {
    this.channel = getSupabase()
      .channel('dev-shared-store')
      .on('broadcast', { event: 'changed' }, ({ payload }) => {
        const tables = (payload as { tables?: unknown })?.tables;
        if (Array.isArray(tables)) onChange(tables.filter((t): t is string => typeof t === 'string'));
      })
      .subscribe();
  }

  private remember(tbl: string, rows: Rows): void {
    const m = new Map<string, string>();
    for (const row of rows) {
      const id = rowKey(row as Record<string, unknown>);
      if (id) m.set(id, JSON.stringify(row));
    }
    this.seen.set(tbl, m);
  }
}

// ---- pictures and recordings ------------------------------------------------

export async function uploadSharedBlob(key: string, blob: Blob): Promise<void> {
  if (!sharedStoreEnabled() || LOCAL_BLOB.test(key)) return;
  const { error } = await getSupabase().storage.from(BUCKET).upload(key, blob, { upsert: true, contentType: blob.type || undefined });
  // The picture is safe on this device either way; only other devices miss it.
  if (error) console.warn(`shared blob ${key} could not be uploaded`, error);
}

export async function downloadSharedBlob(key: string): Promise<Blob | undefined> {
  if (!sharedStoreEnabled() || LOCAL_BLOB.test(key)) return undefined;
  const { data, error } = await getSupabase().storage.from(BUCKET).download(key);
  if (error || !data || data.size === 0) return undefined;
  return data;
}
